import { i18n } from '@/languages';
import type { ICompany } from '@/types';
import { validateCaptcha } from '@/util';
import { hexToThemeColors, useTheme, useToast } from '@skillstown/leap';
import { reactive, ref } from 'vue';

const { t } = i18n.global;
const { createToast } = useToast();
const { applyTheme } = useTheme();

const code = ref('');
const verifyingCode = ref(false);
const registeringUser = ref(false);
const showRegistrationSuccessMessage = ref(false);
const email = ref('');
const firstName = ref('');
const lastName = ref('');
const tokenCompany: Partial<ICompany> = reactive({});

async function handleCodeForm() {
  try {
    verifyingCode.value = true;

    const recaptchaToken = await validateCaptcha('checkRegistrationCode');

    const registrationCode = code.value;

    const adminApiUrl = `${
      import.meta.env.VITE_APP_ADMIN_API_URL
    }/registrationcode/verify`;
    const response = await fetch(adminApiUrl, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        RecaptchaCode: recaptchaToken,
        Code: registrationCode
      })
    });
    const jsonResponse = await response.json();

    if (response.status === 200) {
      Object.assign(tokenCompany, jsonResponse);
      applyTheme(
        hexToThemeColors({
          brand: tokenCompany.brandColor,
          accent: tokenCompany.accentColor || tokenCompany.brandColor
        })
      );
    } else {
      let errorMessage = t('messages.general');
      if (response.status === 404) {
        errorMessage = t('messages.code-not-found');
      } else if (jsonResponse?.RegistrationCode?.length) {
        errorMessage = jsonResponse?.RegistrationCode[0];
      }
      createToast({
        variant: 'error',
        label: t('messages.invalid-code'),
        text: errorMessage
      });
    }
  } catch (error) {
    createToast({
      variant: 'error',
      label: 'Error',
      text: String(error)
    });
  } finally {
    verifyingCode.value = false;
  }
}

async function handleRegistrationForm() {
  try {
    registeringUser.value = true;

    const recaptchaToken = await validateCaptcha('checkRegistrationCode');

    const adminApiUrl = `${
      import.meta.env.VITE_APP_ADMIN_API_URL
    }/users/create-user-by-code`;

    const response = await fetch(adminApiUrl, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: email.value,
        firstName: firstName.value,
        lastName: lastName.value,
        recaptchaToken: recaptchaToken
      })
    });

    if (response.status === 200) {
      showRegistrationSuccessMessage.value = true;
    } else {
      const jsonResponse = await response?.json();
      console.error('Error:', response.status, jsonResponse);
      let errorMessage = t('messages.general');
      if (jsonResponse?.Email?.length) {
        errorMessage = jsonResponse?.Email[0];
      } else if (jsonResponse?.EmailDomain?.length) {
        errorMessage = jsonResponse?.EmailDomain[0];
      }
      createToast({
        variant: 'error',
        label: 'Error',
        text: errorMessage
      });
    }
  } catch (error) {
    createToast({
      variant: 'error',
      label: 'Error',
      text: String(error)
    });
  } finally {
    registeringUser.value = false;
  }
}

export function useRegistration() {
  return {
    code,
    verifyingCode,
    registeringUser,
    showRegistrationSuccessMessage,
    email,
    firstName,
    lastName,
    tokenCompany,
    handleCodeForm,
    handleRegistrationForm
  };
}
